'use client';

import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ICFormAnswers,
  ICQuestions,
  ICStepConfig,
  ICStepName,
  RealEstateUsageAnswer,
} from '@/ui/components/pages/findYourSolution/types';
import ICDropdownSelect from '@/ui/components/pages/findYourSolution/ICDropdownSelect/ICDropDownSelect';
import { CompatibilityScreen } from '@/ui/components/pages/findYourSolution/compatibility-screen/compatibility-screen';
import ICSubscribeInput from '@/ui/components/pages/findYourSolution/EmailInput';
import { useTranslations } from 'next-intl';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { motion } from 'framer-motion';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { useRouter } from 'next/navigation';
import { TrackingConversionType } from '@/utils/tracking-conversion-types';
import { trackConversion } from '@/utils/tracking-helper';
import { useIsClubMember } from '@/ui/hooks/useIsClubMember';
import useFormPersist from 'react-hook-form-persist';
import { useLocalization } from '@/ui/providers/localizationProvider';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import ICInitialScreen from './ICInitialScreen/ICInitialScreen';
import { saveAndTrack, trackCompatibilityScreen, trackCompatibilityScreenUs } from './utils';
import ICSelectWithImages from './ICSelectWithImages/ICSelectWithImages';
import Button from '../../shared/Button';
import ICMultiChoiceSelect from './ICMultichoiceSelect/ICMultiChoiceSelect';
import ICValueInput from './ICValueInput/ICValueInput';
import { ICNotice } from './ICNotice/ICNotice';

export default function InstallationCheckSurvey({
  stepConfigurations,
  isUS = false,
}: {
  stepConfigurations: ICStepConfig[];
  isUS: boolean;
}) {
  const formMethods = useForm<ICFormAnswers>(); // Use FormAnswers type with useForm
  const { locale, country, language } = useLocalization();

  const [storage, setStorage]: any = useState();

  useEffect(() => {
    setStorage(window.sessionStorage);
  }, []);

  useFormPersist('IC-form-answers', {
    watch: formMethods.watch,
    setValue: formMethods.setValue,
    storage,
  });

  const [currentStep, setCurrentStep] = useState<ICStepConfig>(stepConfigurations[0]); // Initialize with first step

  const { localizedLink } = useLocaleLink();
  const router = useRouter();
  const t = useTranslations('IC_Survey');

  const isUserLoggedInClubMember = useIsClubMember();

  const hasSkipButton = 'skippable' in currentStep && currentStep?.skippable;
  const hasBackButton = 'backNavigation' in currentStep && currentStep.backNavigation;

  const icCompleted = async () => {
    await trackConversion(TrackingConversionType.IC_DONE);
  };

  const icStarted = async () => {
    await trackConversion(TrackingConversionType.IC_START);
  };

  // Update URL hash whenever the step changes
  const updateStepInURL = (newStep: ICStepConfig) => {
    window.history.pushState({}, '', `#${newStep.stepName}`);
  };

  // Sync the step from URL on mount and when URL state changes
  useEffect(() => {
    let stepFromUrl = window.location.hash.replace('#', '') as ICStepName;
    let stepConfigFromUrl = stepConfigurations.find((config) => config.stepName === stepFromUrl);

    if (stepConfigFromUrl) {
      setCurrentStep(stepConfigFromUrl);
    } else {
      updateStepInURL(stepConfigurations[0]); // Set to the first step if no valid step in URL
    }

    // Handle back/forward navigation using hashchange event
    const handleHashChange = () => {
      stepFromUrl = window.location.hash.replace('#', '') as ICStepName;
      stepConfigFromUrl = stepConfigurations.find((config) => config.stepName === stepFromUrl);

      if (stepConfigFromUrl) {
        setCurrentStep(stepConfigFromUrl);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  function updateStep(nextStep: ICStepName) {
    const nextICStepConfig = stepConfigurations.find((config) => config.stepName === nextStep);
    if (nextICStepConfig) {
      if (nextICStepConfig.type === 'compatibilityScreen') {
        icCompleted();
      }
      setCurrentStep(nextICStepConfig);
      updateStepInURL(nextICStepConfig);

      if (isUS === true) {
        const formValues = formMethods.getValues();
        trackCompatibilityScreenUs(nextICStepConfig, formValues);
      } else {
        trackCompatibilityScreen(nextICStepConfig, []);
      }
    } else {
      // start from first step in case of error
      setCurrentStep(stepConfigurations[0]);
      updateStepInURL(stepConfigurations[0]);
    }
  }

  // if there's no hash go to first step
  useEffect(() => {
    if (!window.location.hash) {
      updateStep(stepConfigurations[0].stepName);
    }
  }, [stepConfigurations, updateStep]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentStep.stepName]);

  const handlePreviousStep = () => {
    window.history.back();
  };

  const handleNextStep = async (answer: string | string[]) => {
    let nextStep: ICStepName | undefined; // Default next step
    const formValues = formMethods.getValues();

    if ('nextStep' in currentStep && typeof currentStep.nextStep === 'function') {
      nextStep = await currentStep.nextStep({
        answer,
        currentStep,
        formValues,
        isUserNukiClubMember: isUserLoggedInClubMember,
        country,
        locale,
        router,
        icCompleted,
      });
    }

    // Update URL and state
    if (nextStep) {
      updateStep(nextStep);
    }
  };

  const storeAnswer = async (question: string, answer: any) => {
    // Set the form value
    formMethods.setValue(question as keyof ICFormAnswers, answer);

    // if it's the first question, track the start
    if (question === ICQuestions.IC_SURVEY_INTRO || question === ICQuestions.IC_SURVEY_US_START) {
      icStarted();
    }

    // Store the answer in GTM and DB
    saveAndTrack(question, answer);
  };

  const saveStepAnswerAndHandleNextStep = async (question: string, answer: any) => {
    storeAnswer(question, answer);

    // Move to the next step
    await handleNextStep(answer);
  };

  const renderStepComponent = () => {
    switch (currentStep.type) {
      case 'imageSelect':
        return (
          <ICSelectWithImages
            question={t(currentStep.question)}
            mainImage={currentStep.mainImage}
            description={currentStep.description ? t(currentStep.description) : undefined}
            extraInfo={currentStep.extraInfo ? t(currentStep.extraInfo) : undefined}
            extraInfoButton={t('Show info')}
            mainClip={currentStep.mainClip}
            underImageText={currentStep.underImageText ? t(currentStep.underImageText) : undefined}
            options={currentStep.options.map((option) => ({
              ...option,
              text: t(option.text),
              extraInfo: option.extraInfo ? t(option.extraInfo) : undefined,
            }))}
            onSelect={(value) => saveStepAnswerAndHandleNextStep(currentStep.question, value)}
          />
        );
      case 'notice':
        return (
          <ICNotice
            notice={
              currentStep.extraInfo ? (
                <span dangerouslySetInnerHTML={{ __html: t.raw(currentStep.extraInfo) }} />
              ) : undefined
            }
            options={currentStep.options.map((option) => ({
              ...option,
              text: t(option.text),
              extraInfo: option.extraInfo ? t(option.extraInfo) : undefined,
            }))}
            onSelect={(value) => saveStepAnswerAndHandleNextStep(currentStep.question, value)}
          />
        );
      case 'dropdownSelect':
        return (
          <ICDropdownSelect
            question={currentStep.question}
            options={currentStep.options.map((option) => ({
              ...option,
              text: t(option.text),
              extraInfo: option.extraInfo ? t(option.extraInfo) : undefined,
            }))}
            buttonClass="rounded-lg"
            placeholder="Select manufacturer..."
            onSelect={(selectedValues) =>
              saveStepAnswerAndHandleNextStep(currentStep.question, selectedValues)
            }
          />
        );
      case 'emailInput':
        return (
          <ICSubscribeInput
            {...currentStep}
            question={t(currentStep.question)}
            subtext={t(currentStep.subtext)}
            buttonText={currentStep.button ? t(currentStep.button.text) : undefined}
            skipText={currentStep.skipText ? t(currentStep.skipText) : undefined}
            onSkip={() => saveStepAnswerAndHandleNextStep(currentStep.question, 'skip')}
            onEmailSubmit={() => saveAndTrack(currentStep.question, 'done')}
            onGetResults={() => {
              if (isUS) {
                saveStepAnswerAndHandleNextStep(
                  ICQuestions.IC_SURVEY_US_GET_RESULTS,
                  ICQuestions.IC_SURVEY_US_GET_RESULTS,
                );
              } else {
                saveStepAnswerAndHandleNextStep(
                  ICQuestions.IC_SURVEY_GET_RESULTS,
                  ICQuestions.IC_SURVEY_GET_RESULTS,
                );
              }
            }}
          />
        );

      case 'compatibilityScreen': {
        return (
          <CompatibilityScreen
            isUS={isUS}
            installationCheckAnswers={formMethods.getValues()}
            mainText={t(currentStep.mainText)}
            title={formatPunctuation(t(currentStep.question!))}
            button1={
              currentStep.button1
                ? { ...currentStep.button1, text: t(currentStep.button1?.text) }
                : undefined
            }
            button2={
              currentStep.button2
                ? { ...currentStep.button2, text: t(currentStep.button2?.text) }
                : undefined
            }
            subtext1={currentStep.subtext1 ? t(currentStep.subtext1) : undefined}
            subtext2={
              currentStep.subtext2
                ? t.rich(currentStep.subtext2, {
                    email: (chunks) => (
                      <a href={`mailto:${chunks}`} className="underline">
                        {chunks}
                      </a>
                    ),
                  })
                : undefined
            }
            noteTitle={currentStep.noteTitle ? t(currentStep.noteTitle) : undefined}
            learnMore={currentStep.learnMore ? t(currentStep.learnMore) : undefined}
            learnMoreLink={currentStep.learnMoreLink}
            compatibility={currentStep.compatibility}
            language={language}
            b2b={
              isUS
                ? false
                : formMethods.getValues('icSurvey_realEstateUsage') ===
                  RealEstateUsageAnswer.SHORT_RENTAL
            }
            image={currentStep.image}
            onPromoDoubleOptInSubmitted={() => saveAndTrack(currentStep.question!, 'promoSubmit')}
            onPromoVoucherCopy={() => saveAndTrack(currentStep.question!, 'promoCopyVoucher')}
            onPromoLinkClick={() => saveAndTrack(currentStep.question!, 'promoLink')}
            onExtendFeatures={(isTopChoice) => {
              if (isTopChoice) {
                saveAndTrack(currentStep.question!, 'tcExtendFeatures');
              } else {
                saveAndTrack(currentStep.question!, 'extendFeatures');
              }
            }}
            onExtendReasons={(isTopChoice) => {
              if (isTopChoice) {
                saveAndTrack(currentStep.question!, 'tcExtendDesc');
              } else {
                saveAndTrack(currentStep.question!, 'extendDesc');
              }
            }}
            onSelect={(params) => {
              if (params.product) {
                storeAnswer(currentStep.mainText as keyof ICFormAnswers, params.product.name);

                router.push(localizedLink(params.product.path));

                if (params?.product?.isTopChoice) {
                  // track top choice
                  saveAndTrack(currentStep.question!, 'tcAddToCart');
                } else {
                  // track non top choice
                  saveAndTrack(currentStep.question!, 'addToCart');
                }
              } else {
                saveStepAnswerAndHandleNextStep(
                  currentStep.mainText as keyof ICFormAnswers,
                  params.value,
                );
              }
            }}
          />
        );
      }
      case 'initialScreen':
        return (
          <ICInitialScreen
            mainText={t(currentStep.mainText)}
            button={
              currentStep.button
                ? { ...currentStep.button, text: t(currentStep.button?.text) }
                : undefined
            }
            subtext={currentStep.subtext ? t(currentStep.subtext) : undefined}
            image={currentStep.image}
            onSelect={(value) => {
              saveStepAnswerAndHandleNextStep(currentStep.mainText as keyof ICFormAnswers, value);
            }}
          />
        );
      case 'multichoiceSelect':
        return (
          <ICMultiChoiceSelect
            question={currentStep.question}
            options={currentStep.options.map((option) => ({
              ...option,
              text: t(option.text),
              extraInfo: option.extraInfo ? t(option.extraInfo) : undefined,
            }))}
            onSelect={(selectedValues) =>
              saveStepAnswerAndHandleNextStep(currentStep.question, selectedValues)
            }
          />
        );
      case 'valueInput':
        return (
          <ICValueInput
            image={currentStep.image}
            startingValue={currentStep.startingValue}
            unit={currentStep.unit}
            heading={t(currentStep.heading)}
            subtext={t(currentStep.subtext)}
            button={{ ...currentStep.button, text: t(currentStep.button.text) }}
            onSelect={(value) => saveStepAnswerAndHandleNextStep(currentStep.question, value)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-80px)] flex-col px-4 pb-4 pt-8 md:min-h-[auto] lg:px-8 lg:pt-12">
      <FormProvider {...formMethods}>
        <form className="flex flex-grow flex-col">
          <motion.div
            key={currentStep.stepName}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex flex-grow flex-col justify-between"
          >
            {'question' in currentStep && currentStep.type !== 'compatibilityScreen' && (
              <h1 className="mx-auto mb-6 w-full max-w-lg text-center text-2xl font-semibold lg:text-3xl xl:max-w-xl xl:text-4xl">
                {formatPunctuation(t(currentStep.question!))}
              </h1>
            )}
            {renderStepComponent()}

            {(hasSkipButton || hasBackButton) && (
              <div className="mx-auto flex w-full max-w-lg flex-col justify-center gap-4 py-4 lg:h-1/2">
                {hasSkipButton && currentStep.skippable && (
                  <Button
                    variant="whiteBlackBorder"
                    onClick={() => saveStepAnswerAndHandleNextStep(currentStep.question, 'skip')}
                  >
                    {t('Skip')}
                  </Button>
                )}

                {hasBackButton && currentStep.backNavigation && (
                  <Button onClick={handlePreviousStep} variant="whiteBlackBorder">
                    <ChevronLeftIcon className="h-4 w-4" />
                    {t('Back')}
                  </Button>
                )}
              </div>
            )}
          </motion.div>
        </form>
      </FormProvider>
    </div>
  );
}
